import clsx from 'clsx';
import React from 'react';

/**
 * Surface: the card a page's content sits in on the canvas — analytics
 * blocks, list pages, a table with its toolbar. One hairline, one soft
 * resting shadow, 18px corners. Don't nest Surfaces and don't use one for
 * floating layers (`Menu`, `Dialog` have their own) or for full-height
 * workspaces (master/detail), which stay flat against the frame.
 *
 * `overflow-clip` rounds the content's corners without creating a scroll
 * container, so sticky table headers inside still stick to the page.
 */
export interface SurfaceProps extends React.HTMLAttributes<HTMLElement> {
  as?: 'div' | 'section' | 'article';
  /** 20px inner padding for blocks; leave off when a table runs edge to edge. */
  padded?: boolean;
}

export const Surface = React.forwardRef<HTMLElement, SurfaceProps>(
  ({ as: Tag = 'div', padded, className, ...rest }, ref) => (
    <Tag
      ref={ref as React.Ref<HTMLDivElement>}
      className={clsx(
        'min-w-0 overflow-clip rounded-v2-xl border border-v2-line bg-v2-surface shadow-v2-card',
        padded && 'p-5',
        className,
      )}
      {...rest}
    />
  ),
);
Surface.displayName = 'Surface';

/**
 * Card heading row: title · optional count/meta · right-hand action, with an
 * optional one-line description under the title. Sits at the top of a
 * padded Surface.
 */
export const SurfaceHeader: React.FC<{
  id?: string;
  title: React.ReactNode;
  meta?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ id, title, meta, description, action, className }) => (
  <div
    className={clsx(
      'flex min-h-[28px] justify-between gap-6',
      description ? 'items-start' : 'items-center',
      className,
    )}
  >
    <div className="min-w-0">
      <div className="flex min-w-0 items-baseline gap-2">
        <h2
          id={id}
          className="flex items-center gap-2 truncate text-v2-18 font-bold tracking-[-0.015em] text-v2-fg"
        >
          {title}
        </h2>
        {meta}
      </div>
      {description && (
        <p className="mt-0.5 text-v2-13 text-v2-fg-3">{description}</p>
      )}
    </div>
    {action && (
      <div
        className={clsx(
          'flex shrink-0 items-center gap-2',
          description && 'pt-1',
        )}
      >
        {action}
      </div>
    )}
  </div>
);

/** Uppercase micro-label for KPI labels and supporting metadata. Never body copy. */
export const MicroLabel: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <span
    className={clsx(
      'text-v2-10 font-semibold uppercase tracking-[0.1em] text-v2-fg-3',
      className,
    )}
  >
    {children}
  </span>
);
